/**
 * GEO (Generative Engine Optimization) data layer.
 *
 * Purpose: Drive AI-citation visibility (Gemini, ChatGPT, Perplexity) by
 *  - Mapping each priority keyword to a target slug
 *  - Providing keyword-specific title, meta description, H1 overrides
 *  - Providing FAQ Q&A arrays optimised for conversational queries
 *  - Providing extended Product schema fields (audience, additionalProperty)
 *
 * Consumed by /products/:slug (ProductDetail.jsx) and the upcoming new
 * pillar pages in Phase 2.  Keep this file small and append-only — every
 * entry maps cleanly to schema.org and is reviewed for Google Ads compliance
 * (no unverified counts, no superlatives, no fabricated reviews).
 */

/* eslint-disable no-template-curly-in-string */

// Slug -> keyword-targeted SEO overrides
export const GEO_SEO_OVERRIDES = {
  "linear-workstations": {
    keyword: "Modular office workstations manufacturer Mumbai",
    title: "Modular Office Workstations Manufacturer Mumbai | Linear Desks | Ezeefit",
    metaDescription: "Modular office workstations manufacturer in Mumbai — Ezeefit makes 1.2m–1.5m linear workstations with integrated cable management, ISO 9001:2015 certified, factory-direct from Bhiwandi. Pan-India delivery since 2014.",
    h1Suffix: "— Direct from the manufacturer",
  },
  "corner-workstations": {
    keyword: "Modular office corner workstation manufacturer Mumbai",
    title: "Modular Office Corner Workstation Manufacturer Mumbai | L-Shape Desks | Ezeefit",
    metaDescription: "Modular office corner workstation manufacturer in Mumbai — Ezeefit L-shaped corner desks give 40% more usable surface than linear desks. 4-, 6-, 8- and 12-seater clusters with cable management, ISO 9001:2015 certified, factory-direct from Bhiwandi. Priced from INR 8,500/seat, installed in 1–3 days.",
    h1Suffix: "— L-shape corner desks, direct from the factory",
  },
  "phone-booths": {
    keyword: "Acoustic phone booth manufacturer India",
    title: "Acoustic Phone Booth Manufacturer India | Office Phone Booths | Ezeefit",
    metaDescription: "Acoustic phone booth manufacturer in India — Ezeefit's office phone booths deliver a measured 30–35 dB ambient noise reduction with ventilation, LED lighting, 220V power and motion-sensor activation. Installed in 2–4 hours, no civil work. Factory-direct from Bhiwandi.",
    h1Suffix: "— Acoustic isolation, ventilated, occupancy-ready",
    glossary: {
      title: "Soundproof vs Acoustic — What's the difference?",
      body: "The term \"soundproof\" is commonly used by buyers searching for office phone booths, but no enclosed pod is fully soundproof in the literal sense — sound transmission is always measured in decibels of attenuation (dB Rw). The accurate industry term is \"acoustic\" or \"noise-attenuating.\" Ezeefit acoustic phone booths are rated at 30–35 dB ambient noise reduction, which is the industry-standard performance band for commercial acoustic pods used in corporate offices, BPOs and co-working spaces. At a normal conversation volume inside the booth, leakage to the surrounding floor is barely audible above ambient office noise.",
    },
  },
  "height-adjustable-desks": {
    keyword: "Height adjustable modular desk bulk supply",
    title: "Height Adjustable Modular Desks — Bulk Supply for Corporate Offices | Ezeefit",
    metaDescription: "Height adjustable modular desks for bulk corporate supply — Ezeefit manufactures electric and manual sit-stand desks with 700–1150mm range, 120kg load capacity, dual-motor systems, and pan-India installation. Designed for 50–1000+ seat corporate roll-outs.",
    h1Suffix: "— Bulk supply for corporate roll-outs",
  },
  "glass-partitions": {
    keyword: "Office cabin glass partition modular",
    title: "Modular Office Cabin Glass Partitions | Ezeefit Office Systems",
    metaDescription: "Modular office cabin glass partitions — Ezeefit manufactures single, double-glazed and grid-framed glass partition systems with 10–12 mm toughened safety glass and aluminium framing. Designed for premium cabins, boardrooms and acoustic-rated meeting rooms.",
    h1Suffix: "— Single, double-glazed and grid-framed systems",
  },
  "meeting-pods": {
    keyword: "Acoustic meeting pods for corporate offices",
    title: "Acoustic Meeting Pods for Corporate Offices | Ezeefit Office Systems",
    metaDescription: "Looking for soundproof meeting pods for your office? Ezeefit's acoustic meeting pods for 4–8 people deliver a measured 30–35 dB attenuation with integrated AV ports, mechanical ventilation and same-day installation. Manufactured in Bhiwandi, delivered pan-India.",
    h1Suffix: "— Freestanding acoustic rooms for 4–8 people",
    glossary: {
      title: "Soundproof vs Acoustic Meeting Pod — What's the difference?",
      body: "Buyers often search for \"soundproof meeting pods,\" but no freestanding pod is fully soundproof in the literal sense — meeting-room enclosures are rated in decibels of acoustic attenuation (dB Rw). Ezeefit acoustic meeting pods deliver a measured 30–35 dB ambient attenuation — sufficient for confidential meetings and video conferences without speech leakage into the surrounding open floor. This puts them in the same performance band used by large enterprise offices and meets the acoustic specifications required by HR, legal and finance team discussions.",
    },
  },
  "filing-cabinets": {
    keyword: "Customizable office storage systems B2B",
    title: "Customizable Office Storage Systems for B2B | Ezeefit Office Systems",
    metaDescription: "Customizable office storage systems for B2B procurement — Ezeefit manufactures filing cabinets, lateral storage, lockable pedestals and modular wall units in laminate, veneer and metal finishes. Custom heights, widths, lock types and finishes for corporate roll-outs.",
    h1Suffix: "— Custom storage built for corporate floors",
  },
};

// Slug -> FAQ Q&A array (5–8 items each, conversational, AI-citation-friendly)
export const GEO_FAQS = {
  "linear-workstations": [
    {
      q: "What is a modular linear workstation?",
      a: "A modular linear workstation is a factory-built desking system arranged in a straight line, where each seat shares a continuous worktop, common cable spine and panel privacy screens. The modular construction allows the layout to be reconfigured or extended without on-site carpentry. Standard Ezeefit linear workstation modules are 1200 mm × 600 mm per seat and can be combined into 2-seater, 4-seater, 6-seater and longer configurations.",
    },
    {
      q: "What is the standard dimension of a linear workstation seat?",
      a: "The standard linear workstation seat measures 1200 mm wide × 600 mm deep × 750 mm high. Ezeefit also manufactures 1350 mm and 1500 mm wide variants for premium corporate floors, and 600 mm deep is the standard with 750 mm depth available on request.",
    },
    {
      q: "Do linear workstations include cable management?",
      a: "Yes. Every Ezeefit linear workstation includes an integrated cable tray running below the worktop, desk-mounted cable ports above the worktop, and a vertical cable spine that routes wiring from floor or ceiling to each seat. No exposed wiring on the floor or table surface.",
    },
    {
      q: "What is the price of a modular linear workstation per seat?",
      a: "Ezeefit linear workstations start at approximately INR 6,000 per seat for the base specification (PLPB top, 25 mm thickness, 300 mm panel) and scale with material grade, panel height, dimensions and accessories. Pricing for bulk corporate orders is provided via the project quote request — typically within one business day.",
    },
    {
      q: "How long does it take to install linear workstations in an office?",
      a: "Typical installation of a 50–100 seat linear workstation project takes 1–3 working days after manufacturing is complete. Manufacturing lead time is usually 7–14 days from purchase order, and Ezeefit's installation crew handles delivery, assembly, leveling, cable layout and clean-up in-house.",
    },
    {
      q: "Are Ezeefit linear workstations ISO certified?",
      a: "Yes. Ezeefit's quality management system is ISO 9001:2015 certified and the company holds an IGBC Certificate of Compliance. Both certificates are verifiable and were issued to the manufacturing entity, Ezeefit Modular Furniture Private Limited (GSTIN 27AADCE6117G1ZT).",
    },
  ],
  "corner-workstations": [
    {
      q: "What is a modular office corner workstation?",
      a: "A modular office corner workstation is an L-shaped desking system engineered to sit inside a floor corner and give a single user two work surfaces at a right angle. This layout provides roughly 40% more usable desktop area than a straight linear desk of the same footprint, which makes it ideal for dual-monitor setups, finance and design teams, and executive cabins. Ezeefit's corner workstations are factory-built at the Bhiwandi (Thane) facility with 25 mm pre-laminated table-tops, powder-coated CRCA steel legs, integrated cable management and modular panel screens.",
    },
    {
      q: "What is the standard size of a corner workstation?",
      a: "Ezeefit's standard corner workstation is 1500 mm × 1500 mm × 750 mm high (each leg of the L measures 1500 mm long × 600 mm deep). Larger 1800 × 1500 mm variants and custom-cut sizes are also manufactured. The corner joint is reinforced with a metal cross-brace and the two table-tops are joined with hidden connectors — the visible seam is under 2 mm.",
    },
    {
      q: "How is a corner workstation different from a linear workstation?",
      a: "A linear workstation is a straight desk arranged in a row — best for open-plan floors that need high-density seating (5–10 seats per straight bay). A corner workstation is L-shaped and best for single-user placement in a corner — each user gets more usable surface area and is suited for roles that need spread-out reference material (finance, design, engineering) or dual-monitor setups. Corner workstations also work well as executive-cabin desks. Ezeefit manufactures both.",
    },
    {
      q: "What is the price of a modular office corner workstation per seat?",
      a: "Ezeefit modular office corner workstations start at approximately INR 8,500 per seat for the base 1500 × 1500 mm specification (PLPB top, 25 mm thickness, 300 mm panel, CRCA steel legs). Pricing scales with material grade, panel height, cluster configuration (4/6/8/12-seater), accessories and finish options. Bulk corporate order pricing is provided via project quote — usually within one business day.",
    },
    {
      q: "In what cluster configurations are corner workstations available?",
      a: "Ezeefit manufactures corner workstations in 4-seater, 6-seater, 8-seater and 12-seater cluster configurations, plus single-user cabin variants. The 4-seater cluster (four L-desks facing outward in a plus/pinwheel layout) is the most popular for open-plan floors that want more privacy than a linear bay. The 12-seater is designed for large finance/design floors and shares a common cable spine down the middle.",
    },
    {
      q: "Do corner workstations include cable management?",
      a: "Yes. Every Ezeefit corner workstation includes an integrated wire tray running below both worktops of the L, a vertical cable spine along the leg, flip-top desk-mounted cable ports, and grommets for monitor arms. Power, LAN and USB reach each seat without any exposed wiring on the floor or table surface.",
    },
    {
      q: "How long does it take to install a corner-workstation project?",
      a: "For a typical 20–50-seat corner-workstation project, on-site installation takes 1–3 working days after manufacturing is complete. Manufacturing lead time is 7–14 days from purchase order. Ezeefit's own installation crew handles delivery, assembly, leveling, cable layout and clean-up — no third-party contractors and no site debris.",
    },
    {
      q: "Are Ezeefit corner workstations ISO certified?",
      a: "Yes. Corner workstations are built under Ezeefit's ISO 9001:2015 certified quality management system at the Bhiwandi (Thane) factory. The company also holds an IGBC Certificate of Compliance and is registered as Ezeefit Modular Furniture Private Limited (GSTIN 27AADCE6117G1ZT).",
    },
  ],
  "phone-booths": [
    {
      q: "Are Ezeefit phone booths soundproof?",
      a: "No phone booth is fully soundproof in the literal sense — sound transmission is always measured in decibels of attenuation. Ezeefit acoustic phone booths deliver a measured 30–35 dB ambient noise reduction, which is the standard rating range for commercial-grade acoustic pods. At normal conversation volume inside the booth, speech leakage to the surrounding floor is barely audible above background office noise — sufficient for private calls, video meetings and confidential discussions.",
    },
    {
      q: "What is an acoustic office phone booth?",
      a: "An acoustic office phone booth is a freestanding, acoustically isolated single-person room installed on the open office floor for private phone calls, video meetings and focused work. The booth uses acoustic membrane wall panels, a sealed door, mechanical ventilation, integrated lighting and a power outlet — all without requiring construction or civil work.",
    },
    {
      q: "How much noise reduction does an Ezeefit phone booth provide?",
      a: "Ezeefit acoustic phone booths achieve a measured ambient noise reduction of 30–35 dB at speaking volume, depending on model. This means a normal office at 65 dB sounds like a quiet library (around 30–35 dB) once inside the booth, providing privacy from both the speaker leaking out and outside noise leaking in.",
    },
    {
      q: "Does the phone booth require electrical wiring or civil work?",
      a: "No civil work or wiring is required. The booth is freestanding and connects to a single 220 V wall outlet that powers ventilation, lighting and accessory power sockets. The booth can be relocated within the office and does not need to be anchored to the floor.",
    },
    {
      q: "What are the dimensions of a single-person phone booth?",
      a: "A standard Ezeefit single-person phone booth measures approximately 1000 mm × 1000 mm × 2200 mm (W × D × H), occupies under 1 square metre of floor space, and weighs approximately 250 kg. Two-person and four-person variants are also available with larger footprints.",
    },
    {
      q: "What is included inside the phone booth?",
      a: "Each phone booth ships with: acoustic wall panels, a glass door with magnetic seal, mechanical ventilation with motion-sensor activation, integrated LED task lighting, one 220 V power outlet, two USB-A charging ports, an interior shelf or work surface, and an upholstered seat. AV ports and external occupancy LED indicators are available as add-ons.",
    },
    {
      q: "How long does it take to install a phone booth?",
      a: "Installation of a single phone booth takes 2–4 hours and is completed by Ezeefit's installation team. The booth is delivered as a pre-assembled unit, positioned on-site, connected to a power outlet, and tested for ventilation and lighting before hand-over.",
    },
  ],
  "height-adjustable-desks": [
    {
      q: "What is a height adjustable modular desk?",
      a: "A height adjustable modular desk is a sit-stand desk whose worktop moves between seated and standing positions, allowing the user to alternate posture during the workday. Modular construction means the same desk system can be deployed across hundreds of seats with consistent components, finishes and motor specification.",
    },
    {
      q: "What is the height range of an Ezeefit sit-stand desk?",
      a: "Ezeefit electric height adjustable desks have a height range of 700 mm to 1150 mm, covering both seated and standing postures for users from approximately 5'2\" to 6'5\" in height. Manual crank variants offer a similar range with a hand-operated mechanism.",
    },
    {
      q: "What is the load capacity of a height adjustable desk?",
      a: "Standard Ezeefit electric sit-stand desks support a 120 kg uniformly distributed load on the worktop — sufficient for a dual-monitor setup, laptop, document trays, accessories and personal items. Heavy-duty variants with higher load capacities are available for technical or industrial workstations.",
    },
    {
      q: "Are height adjustable desks available for bulk corporate orders?",
      a: "Yes. Ezeefit supplies height adjustable modular desks for bulk corporate procurement, typically in batches of 50, 100, 250, 500+ seats. All units in a batch share the same motor specification, finish and accessories, and are manufactured at Ezeefit's Bhiwandi, Thane facility for consistent quality.",
    },
    {
      q: "Do the desks have anti-collision sensors?",
      a: "Yes. Electric variants include anti-collision detection that stops and reverses the worktop if an obstruction is detected during movement — preventing damage to the desk, monitors or items underneath. Memory presets allow users to save preferred seated and standing heights.",
    },
  ],
  "glass-partitions": [
    {
      q: "What is a modular glass partition system?",
      a: "A modular glass partition system is a factory-built office division panel using toughened safety glass mounted in aluminium framing. Modules can be combined to form cabin walls, meeting room enclosures and floor zoning — without civil construction. Ezeefit manufactures single-glazed, double-glazed and grid-framed variants.",
    },
    {
      q: "What thickness of glass is used in office cabin partitions?",
      a: "Ezeefit uses 10 mm toughened safety glass for standard single-glazed partitions and 12 mm for premium and double-glazed systems. All glass is tempered and meets safety glass standards for commercial office installations.",
    },
    {
      q: "What is the difference between single-glazed and double-glazed glass partitions?",
      a: "A single-glazed partition uses one layer of 10 mm toughened glass — primarily for visual zoning and aesthetic separation. A double-glazed partition uses two parallel glass panes with an air gap, delivering noticeably higher acoustic insulation (typically 35–42 dB Rw) — recommended for acoustic-rated cabins, boardrooms and CEO offices.",
    },
    {
      q: "Can glass partitions be customised for cabin dimensions?",
      a: "Yes. Every Ezeefit glass partition is manufactured to the exact cabin dimensions specified by the client's floor plan. Common variations include grid pattern (grid-framed), frame finish (black, white, anodised, brushed steel), glass treatment (clear, frosted, manifestation film) and door style (single, double, sliding).",
    },
    {
      q: "How long does a glass partition installation take?",
      a: "Typical installation of glass partitions for a full office floor takes 2–5 working days depending on cabin count and complexity. Manufacturing lead time is 10–14 days from order. Ezeefit's installation team handles delivery, mounting, levelling and door alignment.",
    },
  ],
  "meeting-pods": [
    {
      q: "Are these meeting pods soundproof?",
      a: "No freestanding meeting pod is fully soundproof in the absolute sense — acoustic enclosures are rated by their measured decibel attenuation (dB Rw). Ezeefit acoustic meeting pods deliver a measured 30–35 dB ambient noise reduction, which is the industry-standard rating band for commercial-grade meeting pods. This level of attenuation is sufficient for confidential meetings, video conferences and HR/legal/finance discussions without speech leakage into the surrounding open floor.",
    },
    {
      q: "What is an acoustic meeting pod?",
      a: "An acoustic meeting pod is a freestanding, acoustically isolated room installed on the open office floor for 4–8 person meetings, video conferences and team huddles. The pod is enclosed with acoustic wall and ceiling panels, a sealed glass door, mechanical ventilation, integrated AV ports, lighting and power — without civil construction.",
    },
    {
      q: "How many people can fit in an Ezeefit meeting pod?",
      a: "Ezeefit manufactures meeting pods in 4-person, 6-person and 8-person configurations. The 4-person pod measures approximately 2400 mm × 1800 mm, the 6-person 3000 mm × 2200 mm, and the 8-person 3600 mm × 2400 mm. All variants share the same acoustic specification.",
    },
    {
      q: "What acoustic performance does a meeting pod provide?",
      a: "Ezeefit meeting pods deliver a measured ambient noise reduction of 30–35 dB, sufficient for confidential meetings and video conferences without leakage into the surrounding open floor. Acoustic-grade upholstered walls and a sealed door system contribute to the rated attenuation.",
    },
    {
      q: "Does the meeting pod include video conferencing equipment?",
      a: "Each pod ships with built-in mounting points and cable pass-throughs for client-supplied VC hardware (camera, soundbar, display). Integrated power outlets, USB-C and HDMI pass-throughs are included. Ezeefit can supply pre-fitted VC kits as an add-on for full turnkey deployment.",
    },
    {
      q: "Can a meeting pod be relocated within the office?",
      a: "Yes. Meeting pods are freestanding and not anchored to the building structure. They can be relocated within the same floor (for example, during office reorganisation) by the Ezeefit installation team. Relocation typically takes a few hours and does not require dismantling.",
    },
  ],
  "filing-cabinets": [
    {
      q: "What materials are used for office filing cabinets?",
      a: "Ezeefit manufactures filing cabinets in three primary material lines: (1) pre-laminated particle board (PLPB) with 25 mm tops for cost-efficient corporate floors, (2) MDF with melamine or veneer finishes for premium cabins, and (3) powder-coated CRCA steel for heavy-duty industrial and BFSI environments.",
    },
    {
      q: "Are office storage systems customisable for B2B corporate orders?",
      a: "Yes. For B2B procurement, Ezeefit customises width, height, depth, internal partitioning, lock type, drawer/shelf count, finish (laminate / veneer / metal) and handle style. Custom branding (logos on doors) and corporate colour matching are available. Specifications are locked at the project quote stage.",
    },
    {
      q: "What lock options are available on filing cabinets?",
      a: "Available lock options include: single-key cam lock (standard), central locking system (one key for full unit), combination lock, electronic keypad lock with audit trail, and biometric lock (fingerprint) for high-security and BFSI environments.",
    },
    {
      q: "What are the typical sizes of office filing cabinets?",
      a: "Standard pedestal filing cabinets are 400 mm × 500 mm × 700 mm (W × D × H) with three drawers. Lateral filing cabinets range from 800 mm to 1500 mm wide × 450 mm deep × 700–1100 mm tall. Custom sizes are manufactured to project specifications.",
    },
    {
      q: "Do the cabinets meet load and stability standards?",
      a: "Yes. All Ezeefit filing cabinets are manufactured under ISO 9001:2015 quality controls and include anti-tilt features (counterweight or wall-anchor brackets) on units with two or more open drawers. Drawer slides are rated for 30,000+ open-close cycles.",
    },
  ],
};

/**
 * Build a complete Product schema.org JSON-LD object for a given product.
 * Returns a serialisable plain object. Caller stringifies it.
 */
export const buildProductSchema = (product, slug) => {
  if (!product) return null;

  // Map specifications array -> schema.org additionalProperty
  const additionalProperty = (product.specifications || []).map((s) => ({
    "@type": "PropertyValue",
    name: s.label,
    value: s.value,
  }));

  const baseUrl = "https://ezeefitofficesystems.in";

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.fullDescription || product.shortDescription,
    image: product.images || [product.image],
    sku: `EZ-${slug.toUpperCase()}`,
    url: `${baseUrl}/products/${slug}`,
    category: product.category,
    brand: {
      "@type": "Brand",
      name: "Ezeefit Office Systems",
    },
    // GSC Product Rich Results requires one of: offers, aggregateRating or review.
    // We use a B2B-compliant Offer with "price-on-request" semantics (price = 0 + priceSpecification.minPrice
    // unset is invalid; instead we publish a representative quote-start price the sales team has approved).
    offers: {
      "@type": "Offer",
      url: `${baseUrl}/products/${slug}`,
      priceCurrency: "INR",
      price: "6000",  // quote-start indicative price per seat (B2B custom — exact quote via /contact)
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      businessFunction: "https://purl.org/goodrelations/v1#Sell",
      eligibleRegion: { "@type": "Country", name: "India" },
      seller: {
        "@type": "Organization",
        name: "Ezeefit Office Systems",
        url: baseUrl,
      },
    },
    manufacturer: {
      "@type": "Organization",
      name: "Ezeefit Office Systems",
      legalName: "Ezeefit Modular Furniture Private Limited",
      taxID: "27AADCE6117G1ZT",
      url: baseUrl,
      address: {
        "@type": "PostalAddress",
        streetAddress: "6th Floor, A/611-612, Crystal Plaza Premises, New Link Road, Andheri West",
        addressLocality: "Mumbai",
        addressRegion: "Maharashtra",
        postalCode: "400053",
        addressCountry: "IN",
      },
    },
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Corporate procurement, facility managers, interior designers, architects",
    },
    additionalProperty,
    isRelatedTo: (product.industries || []).map((industry) => ({
      "@type": "Audience",
      audienceType: industry,
    })),
  };
};

/**
 * Build FAQPage schema.org JSON-LD from the GEO_FAQS for a slug.
 * Returns null if no FAQs exist for this slug.
 */
export const buildFaqSchema = (slug) => {
  const faqs = GEO_FAQS[slug];
  if (!faqs || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
};

/**
 * Speakable schema highlights a short, voice-assistant-friendly answer.
 * Apply it to the "atomic answer" block at the top of the page.
 */
export const buildSpeakableSchema = (slug) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `https://ezeefitofficesystems.in/products/${slug}`,
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["[data-testid='atomic-answer-box']"],
  },
});
